<template>
  <div
    class="input-text-wrapper"
    :class="{
      'has-warning': !hasValidation,
      'has-success': hasValidation,
      'is-disabled': isDisabled
    }"
  >
    <label v-if="label && label !== ''" :for="elemId" class="input-label">
      <i v-if="isRequired && !hasValidation" class="pi pi-exclamation-triangle text-warning"></i>
      <i v-if="isRequired && hasValidation" class="pi pi-check text-success"></i>
      <span v-if="isRequired" class="required-asterisk">*</span>
      <span>{{ label }}</span>
    </label>

    <div class="input-field-container">
      <input
        :id="elemId"
        ref="inputRef"
        :value="internalValue"
        type="text"
        :placeholder="placeholder"
        :disabled="isDisabled"
        :autofocus="props.autofocus"
        :aria-invalid="isRequired && !hasValidation"
        class="p-inputtext p-component input-qrcode-field"
        :class="{ 'p-invalid': isRequired && !hasValidation, 'is-solid': solidBackground }"
        :data-testid="`${elemId}-input`"
        @input="handleTextInput"
      />

      <button
        type="button"
        class="camera-button"
        :disabled="isDisabled"
        :aria-label="t('Abrir câmera')"
        :data-testid="`${elemId}-camera-open`"
        @click="openCamera"
      >
        <i class="pi pi-camera"></i>
      </button>
    </div>

    <Teleport to="body">
    <div v-if="isCameraOpen" class="camera-overlay">
      <button
        type="button"
        class="camera-close-button"
        :aria-label="t('Fechar câmera')"
        :data-testid="`${elemId}-camera-close`"
        @click="closeCamera"
      >
        <i class="pi pi-times"></i>
      </button>

      <div class="camera-content">
        <p class="camera-title">{{ cameraTitle || t('Aponte para o QR Code') }}</p>

        <video
          v-if="!cameraError"
          ref="videoRef"
          class="camera-video"
          autoplay
          playsinline
          muted
        ></video>
        <canvas ref="canvasRef" style="display:none"></canvas>

        <button
          type="button"
          class="manual-toggle-overlay-button"
          :data-testid="`${elemId}-manual-entry`"
          @click="closeCamera()"
        >
          <i class="pi pi-pencil"></i>
          <span>{{ t('Digitar manualmente') }}</span>
        </button>
      </div>
    </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import jsQR from 'jsqr';

const props = defineProps({
  /** Rendered as the native `autofocus` attribute: PrimeVue Dialog's focus() targets
   *  `[autofocus]` inside its content after the enter transition, so a modal can hand
   *  the focus to this input without racing the dialog (so-search, 26/08/2026). */
  autofocus: { type: Boolean, default: false },
  elemId: {
    type: String,
    required: true
  },
  label: {
    type: String,
    default: ''
  },
  modelValue: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: ''
  },
  required: {
    type: [Boolean, String],
    default: false
  },
  ngRequired: {
    type: [Boolean, String],
    default: false
  },
  validation: {
    type: Function as import('vue').PropType<() => boolean>,
    default: () => true
  },
  disabled: {
    type: [Boolean, String],
    default: false
  },
  cameraTitle: {
    type: String,
    default: ''
  },
  /** Renders the field on the standard white input surface. Off by default so
   *  the scanner shells, which draw their own chrome around a transparent
   *  field, keep their look. */
  solidBackground: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
  'input': [value: string];
  'camera:open': [];
  'camera:close': [];
}>();

const { t } = useI18n();

const inputRef = ref<HTMLInputElement | null>(null);
const videoRef = ref<HTMLVideoElement | null>(null);
const canvasRef = ref<HTMLCanvasElement | null>(null);
const internalValue = ref<string>(props.modelValue || '');
const isCameraOpen = ref<boolean>(false);
const isBarcodeSupported = ref<boolean>(false);
const cameraError = ref<string>('');

let stream: MediaStream | null = null;
let detector: any = null;
let animationFrameId: number | null = null;
let isDetecting = false;

const isRequired = computed(() =>
  props.required === true ||
  props.required === 'true' ||
  props.ngRequired === true ||
  props.ngRequired === 'true'
);

const isDisabled = computed(() =>
  props.disabled === true || props.disabled === 'true'
);

const hasValidation = computed(() => props.validation());

watch(
  () => props.modelValue,
  (newValue) => {
    internalValue.value = newValue || '';
  }
);

function handleUpdate(newValue: any) {
  internalValue.value = newValue;
  emit('update:modelValue', newValue);
  emit('input', newValue);
}

function handleTextInput(event: any) {
  handleUpdate(event.target.value || '');
}

async function openCamera() {
  if (isDisabled.value) {
    return;
  }

  isCameraOpen.value = true;
  emit('camera:open');
  cameraError.value = '';
  isBarcodeSupported.value = typeof window !== 'undefined' && 'BarcodeDetector' in window;

  try {
    await nextTick();

    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      cameraError.value = t('Não foi possível acessar a câmera');
      return;
    }

    if (isBarcodeSupported.value) {
      detector = new (window as any).BarcodeDetector({ formats: ['qr_code'] });
    }

    stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: 'environment'
      },
      audio: false
    });

    if (!videoRef.value) {
      return;
    }

    videoRef.value.srcObject = stream;
    await videoRef.value.play();
    scheduleScan();
  } catch (error) {
    cameraError.value = t('Não foi possível acessar a câmera');
    closeStream();
  }
}

function scheduleScan() {
  cancelScan();

  // The rAF loop must run for BOTH the native BarcodeDetector path and the
  // jsQR canvas fallback. Requiring a detector here meant browsers without
  // BarcodeDetector (iOS Safari, most mobile) never started the loop, so the
  // jsQR fallback in scanFrame() was unreachable. Bail only when neither a
  // detector nor a canvas is available to read frames.
  if (!isCameraOpen.value || !videoRef.value || (!detector && !canvasRef.value)) {
    return;
  }

  animationFrameId = window.requestAnimationFrame(scanFrame);
}

async function scanFrame() {
  if (!isCameraOpen.value || !videoRef.value) {
    return;
  }

  if (isDetecting) {
    scheduleScan();
    return;
  }

  isDetecting = true;

  try {
    if (detector) {
      // Native BarcodeDetector path (Chrome Android)
      const results = await detector.detect(videoRef.value);
      if (results.length > 0 && results[0].rawValue) {
        handleUpdate(results[0].rawValue);
        closeCamera();
        return;
      }
    } else if (canvasRef.value) {
      // jsQR fallback path (Safari iOS, other unsupported browsers)
      const canvas = canvasRef.value;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        canvas.width = videoRef.value.videoWidth;
        canvas.height = videoRef.value.videoHeight;
        if (canvas.width > 0 && canvas.height > 0) {
          ctx.drawImage(videoRef.value, 0, 0);
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const code = jsQR(imageData.data, imageData.width, imageData.height);
          if (code && code.data) {
            handleUpdate(code.data);
            closeCamera();
            return;
          }
        }
      }
    }
  } catch (error) {
    // Ignore transient failures and keep scanning the next frame.
  } finally {
    isDetecting = false;
  }

  scheduleScan();
}

function cancelScan() {
  if (animationFrameId !== null) {
    window.cancelAnimationFrame(animationFrameId);
    animationFrameId = null;
  }
}

function closeStream() {
  if (stream) {
    stream.getTracks().forEach((track) => track.stop());
    stream = null;
  }

  if (videoRef.value) {
    videoRef.value.srcObject = null;
  }
}

function closeCamera() {
  const wasOpen = isCameraOpen.value;
  isCameraOpen.value = false;
  cameraError.value = '';
  detector = null;
  isDetecting = false;
  cancelScan();
  closeStream();
  if (wasOpen) {
    emit('camera:close');
  }
}

onBeforeUnmount(() => {
  closeCamera();
});

const focus = (): void => { inputRef.value?.focus(); };

defineExpose({ openCamera, closeCamera, isCameraOpen, focus });
</script>

<style scoped>
.input-text-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.input-label {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-weight: 500;
  color: var(--p-text-color);
  white-space: nowrap;
}

.required-asterisk {
  color: var(--p-red-500);
  margin-right: 0.25rem;
}

.text-warning {
  color: var(--p-yellow-500);
}

.text-success {
  color: var(--p-green-500);
}

.input-field-container {
  position: relative;
}

.input-qrcode-field {
  width: 100%;
  padding-right: 3rem;
}

.input-qrcode-field.is-solid {
  background: var(--p-surface-0, #fff);
}

.camera-button {
  position: absolute;
  top: 50%;
  right: 0.75rem;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: none;
  border-radius: var(--p-border-radius-md);
  background: transparent;
  color: var(--p-text-muted-color);
  cursor: pointer;
}

.camera-button:hover {
  color: var(--p-text-color);
  background: var(--p-content-hover-background);
}

.camera-button:focus-visible {
  outline: 2px solid var(--p-focus-ring-color);
  outline-offset: 2px;
}

.camera-button:disabled {
  cursor: not-allowed;
}

.has-warning :deep(.p-inputtext) {
  border-color: var(--p-yellow-500);
}

.has-success :deep(.p-inputtext) {
  border-color: var(--p-green-500);
}

.is-disabled {
  opacity: 0.5;
  pointer-events: none;
}

.camera-overlay {
  position: fixed;
  inset: 0;
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background: rgba(0, 0, 0, 0.9);
}

.camera-content {
  width: min(100%, 48rem);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.camera-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--p-surface-0, #ffffff);
}

.camera-video {
  width: 100%;
  height: 80vh;
  border-radius: var(--p-border-radius-xl);
  object-fit: cover;
}

.camera-message {
  margin: 0;
  text-align: center;
  color: var(--p-surface-0, #ffffff);
}

.camera-close-button {
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  border: none;
  background: transparent;
  color: var(--p-surface-0, #ffffff);
  font-size: 1.5rem;
  cursor: pointer;
}

.camera-close-button:focus-visible {
  outline: 2px solid var(--p-surface-0, #ffffff);
  outline-offset: 2px;
}

.manual-toggle-overlay-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.6rem 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: var(--p-border-radius-md);
  background: rgba(255, 255, 255, 0.15);
  color: var(--p-surface-0, #ffffff);
  font-size: 0.9rem;
  cursor: pointer;
  backdrop-filter: blur(4px);
  transition: background 0.15s;
}

.manual-toggle-overlay-button:hover {
  background: rgba(255, 255, 255, 0.3);
}
</style>
